/**
 * Pie / Donut Chart Component
 *
 * Reads the resolver payload (`pie.resolve`). The `is_donut` flag from the
 * backend overrides the explicit `isDoughnut` prop (so an indicator declared
 * with `chart_type=donut` always renders as a donut even if the parent
 * forgot to pass the prop). When `meta.show_total` is true and the chart is
 * a donut, the total is rendered in the centre.
 */

import { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Label,
  Legend,
} from 'recharts';
import { CHART_COLORS } from '../constants';
import { generateMockData, MockPieData } from '../utils';
import { useIndicatorData, IndicatorDataPoint } from './useIndicatorData';
import { useDrilldown } from './useDrilldown';

const LABEL_PERCENT_THRESHOLD = 0.05;

interface PieChartComponentProps {
  isDoughnut?: boolean;
  indicatorId?: number;
  height?: number | string;
}

interface PieDatum extends Record<string, unknown> { name: string; value: number; color: string }

export function PieChartComponent({ isDoughnut = false, indicatorId, height = 250 }: PieChartComponentProps) {
  const { data: live, loading } = useIndicatorData(indicatorId);
  const isPreviewMode = indicatorId === undefined;

  const data = useMemo<PieDatum[]>(() => {
    if (live && Array.isArray(live.data) && live.data.length > 0) {
      return (live.data as IndicatorDataPoint[]).map((d, i) => ({
        name: d.name,
        value: d.value,
        color: typeof d.color === 'string' ? d.color : CHART_COLORS[i % CHART_COLORS.length],
      }));
    }
    if (isPreviewMode) return generateMockData('pie') as MockPieData[];
    return [];
  }, [live, isPreviewMode]);

  const liveDonut = live?.is_donut === true;
  const showAsDonut = liveDonut || isDoughnut;
  const showTotal = Boolean(live?.meta?.show_total) && showAsDonut;
  const drilldown = useDrilldown(live?.meta?.drilldown_url);
  const total = useMemo(
    () => data.reduce((acc, datum) => acc + (Number.isFinite(datum.value) ? datum.value : 0), 0),
    [data],
  );

  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs text-muted-foreground"
        style={{ height: typeof height === 'number' ? `${height}px` : height }}
      >
        {loading ? 'Loading…' : (live?.message || 'No data. Configure axes in the builder.')}
      </div>
    );
  }

  const isLarge = typeof height === 'number' && height > 350;
  // Outside labels overflow narrow preview panels, so the slice radius is
  // shrunk in compact mode and labels are suppressed for slivers below the
  // threshold — the legend below carries the full label set instead.
  const outerR = isLarge ? 160 : 70;
  const innerR = showAsDonut ? (isLarge ? 120 : 50) : 0;

  const renderSliceLabel = (props: Record<string, unknown>) => {
    const percent = typeof props['percent'] === 'number' ? (props['percent'] as number) : 0;
    if (percent < LABEL_PERCENT_THRESHOLD) return '';
    const name = typeof props['name'] === 'string' ? (props['name'] as string) : '';
    return `${(percent * 100).toFixed(0)}% ${name}`;
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          isAnimationActive={false}
          cx="50%"
          cy="50%"
          innerRadius={innerR}
          outerRadius={outerR}
          paddingAngle={2}
          dataKey="value"
          label={renderSliceLabel}
          labelLine={false}
          cursor={live?.meta?.drilldown_url ? 'pointer' : 'default'}
          onClick={(row: unknown) => {
            const name = (row as { name?: unknown })?.name;
            drilldown(name);
          }}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
          {showTotal && (
            <Label
              position="center"
              content={({ viewBox }) => {
                if (!viewBox || !('cx' in viewBox)) return null;
                const cx = (viewBox as { cx: number; cy: number }).cx;
                const cy = (viewBox as { cx: number; cy: number }).cy;
                return (
                  <g>
                    <text x={cx} y={cy - 6} textAnchor="middle" className="fill-foreground" fontSize="14" fontWeight={600}>
                      {total.toLocaleString()}
                    </text>
                    <text x={cx} y={cy + 12} textAnchor="middle" className="fill-muted-foreground" fontSize="11">
                      Total
                    </text>
                  </g>
                );
              }}
            />
          )}
        </Pie>
        <Tooltip />
        <Legend
          verticalAlign="bottom"
          align="center"
          height={isLarge ? 48 : 36}
          iconSize={8}
          wrapperStyle={{ fontSize: '10px', lineHeight: '14px' }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}
